/**
 * Module 'missions': backlog items become missions, missions run sessions (architecture spec
 * section 4, product gap analysis sections 4 and 8.2).
 *
 * Everything here is browser-safe and pure: the hub and the web console derive mission state,
 * progress and blockers with the same functions, so the console stays live from session
 * patches alone and both always agree.
 */
import type { ContinuationRecord, SessionHandoff } from './continuation.ts';
import type { Id, Session, Ts } from '../index.ts';
import type { MissionFlow } from './mission-flow.ts';
import type { FollowUpKind, LaunchFollowUp, MissionIntegration } from './worktree-integrate.ts';
import { INTEGRATE_MAX_ATTEMPTS, INTEGRATE_MAX_LAUNCH_AGE_MS, LOST_NEXT_STEP, newestLaunch } from './worktree-integrate.ts';
import { approvalBlockFor } from './approvals.ts';
import type { ApprovalBlock, ApprovalLike, ApprovalOutcome, ApprovalRequest } from './approvals.ts';
import type { AutopilotStatus } from './autopilot.ts';
import type { CostsState } from './costs.ts';
import type { ItemReview, ItemReviewMode, LandedChange } from './review.ts';
import { trimEndOf } from './trim.ts';
import { LANDING_GATE_LINE } from './standards.ts';

// ---------------------------------------------------------------------------
// Backlog items (requirements: epics, features, stories, tasks, defects, spikes, chores)
// ---------------------------------------------------------------------------

/**
 * The hierarchy is epic > feature > story > task (PARENT_KINDS says which kind may sit under
 * which). Defects link to what they affect; spikes and chores sit anywhere a story could.
 * Items saved as 'bug' by earlier builds load as 'defect'.
 */
export type ItemKind = 'epic' | 'feature' | 'story' | 'task' | 'defect' | 'spike' | 'chore';
export type ItemStatus = 'triage' | 'backlog' | 'ready' | 'in_progress' | 'review' | 'done' | 'dropped';
/** 0 urgent, 1 high, 2 medium, 3 low. */
export type ItemPriority = 0 | 1 | 2 | 3;
export type Severity = 'critical' | 'major' | 'minor' | 'trivial';

export const ITEM_KINDS: readonly ItemKind[] = ['epic', 'feature', 'story', 'task', 'defect', 'spike', 'chore'];
export const ITEM_STATUSES: readonly ItemStatus[] = ['triage', 'backlog', 'ready', 'in_progress', 'review', 'done', 'dropped'];
export const ITEM_PRIORITIES: readonly ItemPriority[] = [0, 1, 2, 3];
export const SEVERITIES: readonly Severity[] = ['critical', 'major', 'minor', 'trivial'];

export const ITEM_KIND_LABEL: Record<ItemKind, string> = {
  epic: 'Epic',
  feature: 'Feature',
  story: 'Story',
  task: 'Task',
  defect: 'Defect',
  spike: 'Spike',
  chore: 'Chore',
};
export const ITEM_STATUS_LABEL: Record<ItemStatus, string> = {
  triage: 'Triage',
  backlog: 'Backlog',
  ready: 'Ready',
  in_progress: 'In progress',
  review: 'In review',
  done: 'Done',
  dropped: 'Dropped',
};
export const ITEM_PRIORITY_LABEL: Record<ItemPriority, string> = { 0: 'Urgent', 1: 'High', 2: 'Medium', 3: 'Low' };
export const SEVERITY_LABEL: Record<Severity, string> = { critical: 'Critical', major: 'Major', minor: 'Minor', trivial: 'Trivial' };

/** The statuses each kind moves through, in order. Dropped is always allowed. */
export const ITEM_WORKFLOW: Record<ItemKind, readonly ItemStatus[]> = {
  epic: ['backlog', 'in_progress', 'done', 'dropped'],
  feature: ['backlog', 'ready', 'in_progress', 'done', 'dropped'],
  story: ['backlog', 'ready', 'in_progress', 'review', 'done', 'dropped'],
  task: ['backlog', 'ready', 'in_progress', 'review', 'done', 'dropped'],
  defect: ['triage', 'ready', 'in_progress', 'review', 'done', 'dropped'],
  spike: ['backlog', 'in_progress', 'review', 'done', 'dropped'],
  chore: ['backlog', 'ready', 'in_progress', 'review', 'done', 'dropped'],
};

/** A status as it reads for one kind: a defect's 'triage' is New, its 'done' is Fixed. */
export function statusLabel(kind: ItemKind, status: ItemStatus): string {
  if (kind === 'defect') {
    if (status === 'triage') return 'New';
    if (status === 'ready') return 'Confirmed';
    if (status === 'done') return 'Fixed';
    if (status === 'dropped') return 'Will not fix';
  }
  return ITEM_STATUS_LABEL[status];
}

/** The status a new item of this kind starts in. */
export function initialStatus(kind: ItemKind): ItemStatus {
  return ITEM_WORKFLOW[kind][0]!;
}

/** The nearest status of `kind`'s workflow to `status` (for a change of kind). */
export function fitStatus(kind: ItemKind, status: ItemStatus): ItemStatus {
  const flow = ITEM_WORKFLOW[kind];
  if (flow.includes(status)) return status;
  const want = ITEM_STATUSES.indexOf(status);
  let best = flow[0]!;
  for (const s of flow) if (s !== 'dropped' && ITEM_STATUSES.indexOf(s) <= want) best = s;
  return best;
}

/** Which kinds may be the parent of each kind. Empty: it sits at the top. */
export const PARENT_KINDS: Record<ItemKind, readonly ItemKind[]> = {
  epic: [],
  feature: ['epic'],
  story: ['epic', 'feature'],
  task: ['story', 'defect', 'feature'],
  defect: ['epic', 'feature'],
  spike: ['epic', 'feature', 'story'],
  chore: ['epic', 'feature', 'story'],
};

/** Kinds a mission can work directly. Epics and features run as a sequence of their children. */
export const MISSION_KINDS: readonly ItemKind[] = ['story', 'task', 'defect', 'spike', 'chore'];

/** Map an older or looser kind name onto a kind ('bug' became 'defect'). */
export function normalizeKind(v: unknown): ItemKind | null {
  if (v === 'bug') return 'defect';
  return ITEM_KINDS.includes(v as ItemKind) ? (v as ItemKind) : null;
}

/**
 * Fill the fields an item stored by an earlier build may lack ('bug' becomes a defect), so
 * old files and rows still load. Used by the hub's store and the control plane.
 */
export function normalizeItem(i: Partial<BacklogItem> & Pick<BacklogItem, 'id'>): BacklogItem {
  const kind = normalizeKind(i.kind) ?? 'story';
  const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
  return {
    key: '',
    projectId: '',
    title: '',
    body: '',
    status: initialStatus(kind),
    priority: 2,
    owner: null,
    parentId: null,
    rank: typeof i.createdAt === 'number' ? i.createdAt : 0,
    estimate: null,
    defect: kind === 'defect' ? { severity: 'major', environment: '', steps: '', expected: '', actual: '' } : null,
    source: null,
    aiDrafted: false,
    statusNote: null,
    statusPinned: false,
    createdAt: 0,
    updatedAt: 0,
    ...i,
    kind,
    acceptance: arr(i.acceptance),
    labels: arr(i.labels),
    components: arr(i.components),
  };
}

/** One acceptance criterion: Given/When/Then, or a plain checklist line. Each is checked off on its own. */
export interface AcceptanceCriterion {
  /** Stable within its item ('ac1', 'ac2', ...). */
  id: string;
  format: 'gwt' | 'check';
  /** The checklist line; for Given/When/Then a one-line reading of the three parts. */
  text: string;
  given?: string;
  when?: string;
  then?: string;
  done: boolean;
  /** Who checked it: a person, or 'mission:<id>' when a mission's final report did. */
  checkedBy?: string | null;
  checkedAt?: Ts | null;
  /** What the mission reported for it. */
  evidence?: string | null;
}

export interface DefectDetails {
  severity: Severity;
  environment: string;
  steps: string;
  expected: string;
  actual: string;
}

/** Where a requirement came from (traceability). */
export interface ItemSource {
  kind: 'document' | 'signal' | 'ai';
  /** The document's file name, or the signal's title. */
  name: string;
  /** The heading path of the section it came from: "3 Payments > 3.2 Refunds". */
  section?: string;
  /** Section ids from the import ("S3", "S3.2"). */
  sections?: string[];
  attachmentId?: Id;
  proposalId?: Id;
  /** For a defect suggested from a runtime signal: what it was and where. */
  signal?: { kind: SignalKind; ref: string; machineId?: Id };
}

/** Why the status is what it is, when a mission or an override set it. */
export interface StatusNote {
  text: string;
  by: string;
  at: Ts;
  kind: 'mission' | 'override';
  missionId?: Id;
}

export interface BacklogItem {
  id: Id;
  /** Human key, unique per project: QAWL-123. */
  key: string;
  projectId: Id;
  kind: ItemKind;
  title: string;
  /** Markdown. */
  body: string;
  status: ItemStatus;
  priority: ItemPriority;
  owner: string | null;
  parentId: Id | null;
  /** Order among siblings (lower first): the document view and epic sequences follow it. */
  rank: number;
  acceptance: AcceptanceCriterion[];
  /** Story points. */
  estimate: number | null;
  labels: string[];
  components: string[];
  /** Defects only. */
  defect: DefectDetails | null;
  source: ItemSource | null;
  /** True when an AI wrote the item and a person accepted it. */
  aiDrafted: boolean;
  statusNote: StatusNote | null;
  /**
   * A person overrode the status while a mission was running (audited): missions stop moving
   * it until the next start.
   */
  statusPinned: boolean;
  /** Orgus's review of the newest landed mission (ORGU-85, review.ts); absent until one ran. */
  review?: ItemReview | null;
  /** When it last became done (Finished this week counts these); absent on items done before it was kept. */
  doneAt?: Ts | null;
  /**
   * QA-85: a commit on the project main named this item's key (outside a mission, say). Autopilot
   * skips the item while it is set; it never closes the item (a commit may be partial). A person
   * clears it with `cited: null` to hand the item back.
   */
  cited?: ItemCitation | null;
  createdAt: Ts;
  updatedAt: Ts;
}

/** A main commit that names an item's key (QA-85). */
export interface ItemCitation {
  sha: string;
  /** The commit subject, clipped to 200 characters. */
  subject: string;
  at: Ts;
}

/** Item keys (ORGU-148 style) a commit message names, word-bounded, upper case, each once. */
export function citedKeys(message: string): string[] {
  const out = new Set<string>();
  for (const m of message.matchAll(/(?<![A-Za-z0-9_-])([A-Z][A-Z0-9]*-\d+)(?![A-Za-z0-9_-])/g)) out.add(m[1]!);
  return [...out];
}

export type AcceptanceInput = Pick<AcceptanceCriterion, 'format' | 'text'> & Partial<Pick<AcceptanceCriterion, 'id' | 'given' | 'when' | 'then' | 'done'>>;

/** POST /api/missions/items */
export interface ItemInput {
  projectId: Id;
  kind: ItemKind;
  title: string;
  body?: string;
  status?: ItemStatus;
  priority?: ItemPriority;
  owner?: string | null;
  parentId?: Id | null;
  rank?: number;
  acceptance?: AcceptanceInput[];
  estimate?: number | null;
  labels?: string[];
  components?: string[];
  defect?: Partial<DefectDetails> | null;
  source?: ItemSource | null;
  aiDrafted?: boolean;
}

/**
 * PATCH /api/missions/items/:id. While a mission runs the item, a status change needs
 * `override: { reason }`: it is audited and pins the status until the next start.
 * `pinned: false` hands the status back to its missions.
 */
export type ItemPatch = Partial<Omit<ItemInput, 'projectId' | 'source'>> & {
  override?: { reason: string };
  pinned?: false;
  /** `null` removes the main-commit citation (QA-85) so Autopilot takes the item again. */
  cited?: null;
};

/** POST /api/missions/items/bulk: the same change on many items (one audit entry each). */
export interface ItemBulkPatch {
  ids: Id[];
  patch: Pick<ItemPatch, 'status' | 'priority' | 'owner' | 'parentId' | 'estimate'> & { addLabels?: string[]; removeLabels?: string[] };
}

/** POST /api/missions/items/bulk-delete: delete many items (children before their parents). */
export interface ItemBulkDelete {
  ids: Id[];
}

/** What a bulk delete did: each id deleted, or skipped with the reason. */
export interface ItemBulkDeleteResult {
  deleted: Id[];
  skipped: { id: Id; reason: string }[];
}

// ---------------------------------------------------------------------------
// Links between items
// ---------------------------------------------------------------------------

/** Stored in one direction; the other end reads the inverse ("Blocked by"). */
export type LinkType = 'blocks' | 'relates' | 'duplicates' | 'implements' | 'affects';
export const LINK_TYPES: readonly LinkType[] = ['blocks', 'relates', 'duplicates', 'implements', 'affects'];
export const LINK_LABEL: Record<LinkType, { out: string; in: string }> = {
  blocks: { out: 'Blocks', in: 'Blocked by' },
  relates: { out: 'Relates to', in: 'Relates to' },
  duplicates: { out: 'Duplicates', in: 'Duplicated by' },
  implements: { out: 'Implements', in: 'Implemented by' },
  affects: { out: 'Affects', in: 'Affected by' },
};

export interface ItemLink {
  id: Id;
  type: LinkType;
  from: Id;
  to: Id;
  by: string;
  at: Ts;
}

/** POST /api/missions/links. `inverse` reads it from the other end: "from is blocked by to". */
export interface LinkInput {
  type: LinkType;
  from: Id;
  to: Id;
  inverse?: boolean;
}

// ---------------------------------------------------------------------------
// Project keys, saved views, history
// ---------------------------------------------------------------------------

/** Per project: the key prefix and the next number (QAWL-124). */
export interface ProjectKey {
  prefix: string;
  next: number;
}

export type BacklogViewKind = 'tree' | 'board' | 'table' | 'doc' | 'defects';
export const BACKLOG_VIEWS: readonly BacklogViewKind[] = ['tree', 'board', 'table', 'doc', 'defects'];
export const BACKLOG_VIEW_LABEL: Record<BacklogViewKind, string> = { tree: 'Tree', board: 'Board', table: 'Table', doc: 'Document', defects: 'Defects' };

export interface ViewFilter {
  kinds?: ItemKind[];
  statuses?: ItemStatus[];
  priorities?: ItemPriority[];
  severities?: Severity[];
  labels?: string[];
  owner?: string | null;
  q?: string;
  /** Only items with quality findings. */
  issues?: boolean;
}

export interface SavedView {
  id: Id;
  name: string;
  projectId: Id | null;
  view: BacklogViewKind;
  filter: ViewFilter;
  sort?: { key: string; dir: 'asc' | 'desc' };
  by: string;
  createdAt: Ts;
}

export type SavedViewInput = Pick<SavedView, 'name' | 'projectId' | 'view' | 'filter'> & { sort?: SavedView['sort'] };

/** One change to an item (GET /api/missions/items/:id/history, newest last). */
export interface ItemHistoryEntry {
  at: Ts;
  by: string;
  /** item.create, item.update, item.override, item.sync, link.add, link.remove, ac.check */
  action: string;
  field?: string;
  from?: unknown;
  to?: unknown;
  note?: string;
  /** ORGU-155: a change Autopilot's grooming applied ('autopilot.groom'), undone at POST /api/missions/autopilot/groomed/:groomId/undo. Additive. */
  groomId?: Id;
}

// ---------------------------------------------------------------------------
// Proposals: what an AI suggests, reviewed by a person before anything is created
// ---------------------------------------------------------------------------

export type ProposalKind = 'import' | 'draft' | 'split' | 'edit' | 'links' | 'defect';
export type Decision = 'pending' | 'accepted' | 'rejected';

export interface ProposedItem {
  /** Unique within the proposal. */
  ref: string;
  /** Another proposed item's ref, or empty. */
  parentRef: string;
  /** An existing item to sit under when parentRef is empty. */
  parentId?: Id | null;
  kind: ItemKind;
  title: string;
  body: string;
  acceptance: AcceptanceInput[];
  priority: ItemPriority;
  estimate: number | null;
  labels: string[];
  defect: DefectDetails | null;
  /** Section ids of the source document ("S3.2"). */
  sections: string[];
  /** Why the AI suggests it. */
  note?: string;
  decision: Decision;
  /** Set after apply. */
  createdId?: Id;
}

export interface ProposedEdit {
  itemId: Id;
  patch: Pick<ItemPatch, 'title' | 'body' | 'acceptance' | 'estimate' | 'labels' | 'defect'>;
  rationale: string;
  decision: Decision;
}

export interface ProposedLink {
  from: Id;
  to: Id;
  type: LinkType;
  rationale: string;
  decision: Decision;
}

export interface SourceSection {
  /** "S3.2" */
  id: string;
  title: string;
  level: number;
  /** The section's text (clipped). */
  text: string;
}

export interface Proposal {
  id: Id;
  kind: ProposalKind;
  projectId: Id;
  title: string;
  by: string;
  createdAt: Ts;
  updatedAt: Ts;
  /** The AI job that wrote it. */
  jobId: Id | null;
  status: 'open' | 'applied' | 'discarded';
  items: ProposedItem[];
  edits: ProposedEdit[];
  links: ProposedLink[];
  questions: string[];
  assumptions: string[];
  /** The document an import read. */
  source: { name: string; attachmentId?: Id; sections: SourceSection[]; chars: number } | null;
  /** split and edit: the item it is about. */
  targetItemId: Id | null;
  /** split: drop the original once its parts are created. */
  dropOriginal?: boolean;
  /** defect: the signal it came from. */
  signalId?: Id;
  /** draft: the files it was written from (attached to the first item created). */
  attachmentIds?: Id[];
  /** import: add the open questions to the first item created (default true). */
  keepQuestions?: boolean;
  appliedAt?: Ts;
  appliedBy?: string;
  createdIds?: Id[];
}

/** PATCH /api/missions/proposals/:id: what a person changes while reviewing. */
export interface ProposalPatch {
  items?: ProposedItem[];
  edits?: ProposedEdit[];
  links?: ProposedLink[];
  dropOriginal?: boolean;
  keepQuestions?: boolean;
}

// ---------------------------------------------------------------------------
// Suggested defects from runtime signals
// ---------------------------------------------------------------------------

export type SignalKind = 'pipeline' | 'job' | 'session' | 'verify';
export const SIGNAL_KINDS: readonly SignalKind[] = ['pipeline', 'job', 'session', 'verify'];
export const SIGNAL_KIND_LABEL: Record<SignalKind, string> = { pipeline: 'Failed pipeline item', job: 'Failed command', session: 'Session error', verify: 'Failed verify' };

export interface DefectSignal {
  id: Id;
  kind: SignalKind;
  /** Stable reference to what failed, so one failure is suggested once: "job:<id>". */
  ref: string;
  projectId: Id;
  machineId: Id | null;
  title: string;
  /** What was seen: the error, the log tail. Attached to the defect as a file. */
  evidence: string;
  at: Ts;
  status: 'open' | 'accepted' | 'dismissed';
  /** The defect created from it. */
  itemId?: Id;
  decidedBy?: string;
  decidedAt?: Ts;
}

// ---------------------------------------------------------------------------
// Sequences: an epic's children worked one mission after another
// ---------------------------------------------------------------------------

export interface MissionSequence {
  id: Id;
  /** The epic or feature whose children run. */
  itemId: Id;
  projectId: Id;
  /** The children, in the order they run. */
  itemIds: Id[];
  /** The mission of each child once started (same index). */
  missionIds: (Id | null)[];
  /** Index of the child running now. */
  index: number;
  state: 'running' | 'done' | 'halted' | 'stopped';
  /** Why it halted. */
  reason?: string;
  start: SequenceStart;
  by: string;
  createdAt: Ts;
  updatedAt: Ts;
}

export type SequenceStart = Pick<MissionStartRequest, 'machineId' | 'checkoutId' | 'cwd' | 'model' | 'permissionMode'> & { mode?: 'headless' };

/** POST /api/missions/sequences */
export interface SequenceInput {
  itemId: Id;
  /** Children to run, in order; default every open child a mission can work, by rank. */
  itemIds?: Id[];
  start: SequenceStart;
}

// ---------------------------------------------------------------------------
// Missions
// ---------------------------------------------------------------------------

export type MissionState = 'planned' | 'running' | 'waiting' | 'done' | 'failed' | 'stopped';
export const MISSION_STATES: readonly MissionState[] = ['planned', 'running', 'waiting', 'done', 'failed', 'stopped'];
export const MISSION_STATE_LABEL: Record<MissionState, string> = {
  planned: 'Planned',
  running: 'Running',
  waiting: 'Waiting for you',
  done: 'Done',
  failed: 'Failed',
  stopped: 'Stopped',
};

/** Audit labels for the actions this module records (their `action` is not an ActionKind). */
export const MISSIONS_ACTION_LABEL: Record<string, string> = {
  'item.create': 'Add item',
  'item.update': 'Edit item',
  'item.delete': 'Delete item',
  'mission.create': 'Create mission',
  'mission.update': 'Edit mission',
  'mission.delete': 'Delete mission',
  'mission.start': 'Start mission',
  'mission.stop': 'Stop mission',
  'mission.close': 'Close mission',
  'mission.reopen': 'Reopen mission',
  'mission.fail': 'Mission failed',
  'mission.halt': 'Mission stopped at a request',
  'mission.continue': 'Continue fresh from the handoff',
  'mission.recover': 'Start again after Orgus restarted',
  'item.override': 'Override item status',
  'item.sync': 'Item status follows mission',
  'item.bulk': 'Edit items in bulk',
  'link.add': 'Link items',
  'link.remove': 'Unlink items',
  'ac.check': 'Check acceptance criterion',
  'view.save': 'Save view',
  'view.delete': 'Delete view',
  'key.update': 'Change key prefix',
  'proposal.create': 'AI proposal',
  'proposal.apply': 'Apply AI proposal',
  'proposal.discard': 'Discard AI proposal',
  'signal.accept': 'Accept suggested defect',
  'signal.dismiss': 'Dismiss suggested defect',
  'sequence.start': 'Start sequence',
  'sequence.advance': 'Sequence starts next item',
  'sequence.stop': 'Stop sequence',
  'import.start': 'Import document',
};

/** One 'Start mission': a session.start the hub ran for this mission. */
export interface MissionLaunch {
  /** The audit entry id when the action reached a machine, else a local id. */
  id: Id;
  machineId: Id;
  at: Ts;
  by: string;
  mode: 'headless' | 'terminal';
  model: string | null;
  checkoutId: Id | null;
  /** The folder the session runs in, as the machine reported it. */
  cwd: string | null;
  /** The worktree the start created, if any. */
  worktree: string | null;
  /** The Claude Code session id, once known. Terminal launches learn it from the session title. */
  sessionId: Id | null;
  /** The agent kind it started (absent: Claude Code, and launches before 19 Oct 2026). */
  agentKind?: string;
  /** Why the scheduler picked that kind (and model) for an 'auto' mission, in one line. Additive, 19 Oct 2026. */
  agentReason?: string;
  /** The CLI version the machine reported for that kind at the start (R-PL-6: evidence per setup). Additive, 30 Oct 2026. */
  agentVersion?: string;
  /**
   * 'pending': the machine did not answer in time, so the start may still be running there. The
   * session is adopted by its mission marker when it appears (or by the machine's late answer),
   * and the launch becomes 'ok'; a late refusal makes it 'error'.
   */
  result: 'ok' | 'error' | 'pending';
  /**
   * The worktree's head when the hub first saw its checkout (QA-34, 28 Oct 2026): a branch whose
   * head later moved has work, so finding it on main means it landed outside Orgus's lander.
   */
  firstHead?: string;
  /** The hub saw the worktree's branch ahead of main at least once (QA-34). */
  sawAhead?: boolean;
  message?: string;
  /** Attachments copied to the machine for this start (module 'attachments'), when there were any. */
  attachments?: { sent: number; failed: number; message?: string };
  /**
   * The branch the session works on: the new worktree's, else the one the machine reported, else
   * its checkout's (launchExtras). Absent on launches recorded by earlier builds.
   */
  branch?: string | null;
  /** What the person added for this start (Send back with a note), trimmed (launchExtras). */
  note?: string;
  /** A fresh continuation of an earlier session from its handoff, with the comparison (ADR 0062 decision 2a). */
  continuation?: ContinuationRecord;
  /** Orgus started this launch to resolve an integration setback of an earlier one (QA-58). */
  followUp?: LaunchFollowUp;
  /**
   * Orgus started it again because its previous session lost Orgus's hooks while Orgus restarted
   * (QA-102, 30 Oct 2026): every tool call was refused for a moment and the session gave up.
   * 'agent-switch': its previous agent failed for a reason of its own (a crash, signed out, a
   * usage limit) and Orgus started the mission again on another agent (QA-113, 30 Oct 2026).
   */
  recovered?: 'orgus-restart' | 'agent-switch';
}

/** The longest note a launch keeps; the prompt carries the whole of it. */
export const LAUNCH_NOTE_MAX = 500;

/**
 * The facts a launch records beside the machine's answer: the branch the session works on (the
 * new worktree's, else the one the machine reported, else its checkout's) and the person's note.
 * Shared by the hub and the control plane so both record the same.
 */
export function launchExtras(req: Pick<MissionStartRequest, 'newWorktree' | 'note'>, data: { branch?: unknown }, checkoutBranch?: string | null): Pick<MissionLaunch, 'branch' | 'note'> {
  const fromReq = req.newWorktree?.branch?.trim();
  const reported = typeof data.branch === 'string' && data.branch.trim() ? data.branch.trim() : null;
  const branch = fromReq || reported || checkoutBranch || null;
  const note = req.note?.trim();
  return { branch, ...(note ? { note: note.length > LAUNCH_NOTE_MAX ? `${note.slice(0, LAUNCH_NOTE_MAX - 1)}…` : note } : {}) };
}

export type MissionKind = 'work' | 'review';

export interface MissionPullRequest {
  repo: string;
  number: number;
  title: string;
  branch: string;
  headSha: string;
  baseBranch?: string;
  baseSha?: string;
  url?: string;
  author?: string;
}

export interface Mission {
  id: Id;
  itemId: Id | null;
  /** ORGU-244: kind of mission ('work' for backlog items, 'review' for pull request reviews). */
  kind?: MissionKind;
  /** ORGU-244: pull request this mission reviews, when kind is 'review'. */
  pullRequest?: MissionPullRequest | null;
  /** ORGU-299: Watch incident this mission fixes or remedies, if any. */
  incidentId?: Id | null;
  projectId: Id;
  title: string;
  goal: string;
  owner: string;
  /** Derived from its sessions on every read (see deriveMission); stored as last seen. */
  state: MissionState;
  /** 0..1, derived like state. */
  progress: number;
  /** Sessions this mission started (session ids as Claude Code reports them). */
  sessionIds: Id[];
  launches: MissionLaunch[];
  /** Where to run: a machine id, or required machine labels. */
  placement: { machineId?: Id; labels?: string[] };
  /** Default model for its sessions; null: the machine default. */
  model: string | null;
  /**
   * USD budget for the whole mission, if any. When the costs module's spend reaches it, the
   * mission shows a budget warning; it stops sessions only with `budgetHardStop`.
   */
  budget: number | null;
  /**
   * Opt in (QA-63): when the spend passes `budget`, Orgus stops the mission's session and the
   * mission reads Stopped with the reason. Default false; absent on missions stored before it.
   */
  budgetHardStop?: boolean;
  /**
   * Set when a person closes the mission, or Orgus when its branch landed (`by: 'orgus'`, a note
   * naming the branch and commit); it then keeps that state whatever its sessions do.
   */
  /** `reason: 'budget'`: Orgus stopped it over its hard budget (ORGU-265). */
  closed: { as: 'done' | 'stopped'; at: Ts; by: string; note?: string; reason?: 'budget' | 'nothing-to-land' } | null;
  /**
   * The landing queue landed a branch one of its sessions submitted (POST /api/sessions/:id/landed,
   * ADR 0055). Its sessions' input is closed; the mission closes as done once they have all ended,
   * and until then a finished turn reads running (wrapping up), never waiting for a person.
   */
  landed?: ({ branch: string; sha: string; sessionId: Id; at: Ts; by: string } & Pick<LandedChange, 'base' | 'commits' | 'files'>) | null;
  /** The last mission state its item's status followed (the hub moves the item once per change). */
  syncedState?: MissionState | null;
  /**
   * The final report, once the mission finished: what it said about each acceptance criterion.
   * Also read while the mission is blocked on an approval and its session has stopped (`blocked`),
   * so a person can decide with it; only a finished mission's report checks criteria off.
   */
  report?: MissionReport | null;
  /** The epic sequence this mission is a step of. */
  sequenceId?: Id | null;
  /** Autopilot created and started it (ADR 0073): it holds one of the project's Autopilot slots. */
  autopilot?: boolean;
  /**
   * The handoff of its newest session, written from the transcript at each progress point
   * (ADR 0062 decision 2a): what a fresh continuation starts from. Hub side, never in the repo.
   */
  handoff?: SessionHandoff | null;
  /**
   * Where it is in Prepare, Work, Integrate, Verify, Land, Review, Done, who acts next and the
   * steps so far (ADR 0086, ADR 0094): derived by the hub on every publish from the facts above,
   * never stored. Absent from an older hub; the console derives the same (missionFlowFrom).
   */
  flow?: MissionFlow | null;
  /**
   * Orgus committing, merging main and landing the finished work of the newest launch (ADR 0086
   * phase 1, `worktree.integrate`): status, tries and steps. Hub kept; absent until Orgus tries.
   */
  integration?: MissionIntegration | null;
  /**
   * A person paused this mission (ADR 0103): Orgus starts, resumes and wakes nothing for it until
   * a person resumes it. Its running session finishes its current turn and waits.
   */
  paused?: MissionPause | null;
  /**
   * Published only (ADR 0103): this ended mission stands for `count` ended missions with the same
   * title and outcome, the oldest from `firstAt` (foldEndedMissions). Never stored.
   */
  repeats?: { count: number; firstAt: Ts } | null;
  createdAt: Ts;
  updatedAt: Ts;
}

/** Who paused missions, and when (ADR 0103). */
export interface MissionPause {
  by: string;
  at: Ts;
}

export interface MissionReport {
  at: Ts;
  /** The session's last message (clipped). */
  text: string;
  /** Criterion id -> what the report said. */
  acceptance: { id: string; met: boolean; evidence: string }[];
  /**
   * Read while the mission was blocked on an approval (R-HL-3): the session's own account, shown
   * as such; it checked nothing off. Read again, and applied, once the mission is done.
   */
  blocked?: boolean;
  /**
   * The session it was read from, and that session's last activity then: a later turn of it, or a
   * newer session, is read again. Absent on reports read before 19 Oct 2026.
   */
  sessionId?: Id;
  activity?: Ts;
  /**
   * QA-62: steps the agent left for a person (its "NEEDS A PERSON:" lines): publishing, pushing,
   * anything it must not do itself. The finished part lands; the item waits for a person.
   */
  needsPerson?: string[];
}

/** The line a final report uses for a step the agent left for a person (MISSION_PERSON_LINE). */
const NEEDS_PERSON_RE = /^[\s>*_`#-]*needs a person[*_`]*\s*[:\-]\s*(.+)$/gim;

/** QA-62: the steps a final report left for a person, in order, each one plain line (at most 5). */
export function parsePersonSteps(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(NEEDS_PERSON_RE)) {
    const step = m[1]!.replace(/[*_`]+/g, '').replace(/\s+/g, ' ').trim();
    if (step && !out.includes(step)) out.push(step.length > 200 ? `${step.slice(0, 199)}…` : step);
  }
  return out.slice(0, 5);
}

/** QA-62: the item's status note while it waits for a step its mission left for a person. */
export function personStepsNote(missionTitle: string, steps: readonly string[]): string {
  const more = steps.length > 1 ? ` (and ${steps.length - 1} more)` : '';
  return `Mission "${missionTitle}" finished the rest. Needs a person: ${steps[0]!.replace(/[.\s]+$/, '')}${more}.`;
}

/**
 * What Orgus makes of a finished mission's final report (ADR 0078):
 *   met      every acceptance criterion reported met (and, unless it landed, nothing says it did not finish)
 *   unmet    a criterion failed, or the message says the work did not finish (incomplete, not landed,
 *            blocked, still running in the background)
 *   unknown  neither: a person reviews it
 */
export type MissionVerdict = 'met' | 'unmet' | 'unknown';

export interface MissionVerdictResult {
  verdict: MissionVerdict;
  /** 'unmet': why, a sentence that reads after "The session ended without finishing." */
  why?: string;
}

/** Who a sentence about unfinished work is about: the goal, the mission, an item key, it. */
const WORK_SUBJECT = String.raw`(?:the\s+)?(?:goal|mission|work|story|task|defect|item|change|changes|it|this|[a-z][a-z0-9]*-\d+)`;

/**
 * Sentences a final message uses to say the work did not finish. Dogfood, 19 Oct 2026: 70 of 76
 * missions read Done while their last message said "The goal remains incomplete", "ORGU-121 is
 * incomplete and has not landed", "Goal not met; nothing landed", "The mission is blocked by tool
 * execution failures" or "test:affected is running in the background. Once it passes I'll land".
 */
const UNFINISHED_RE = new RegExp(
  [
    String.raw`\b${WORK_SUBJECT}\s+(?:is|are|remains|remain|was|were|has\s+been)\s+(?:still\s+)?\**\s*(?:incomplete|unfinished|not\s+(?:yet\s+)?(?:complete|completed|done|finished|met|landed|achieved))\b`,
    String.raw`\b(?:goal|mission)\s+(?:is\s+|was\s+)?not\s+(?:yet\s+)?(?:met|complete|completed|done|finished|achieved)\b`,
    String.raw`\bnot\s+(?:yet\s+)?(?:been\s+)?(?:confirmed\s+)?landed\b`,
    String.raw`\bnothing\s+(?:was\s+|has\s+been\s+)?landed\b`,
    String.raw`\b${WORK_SUBJECT}\s+(?:is|was|remains)\s+blocked\b`,
    String.raw`\bI\s+could\s+not\s+(?:land|commit|finish|complete)\b`,
    String.raw`\b(?:is|are)\s+(?:still\s+|now\s+)?running\s+in\s+the\s+background\b`,
    String.raw`\bonce\s+(?:it|they|that|this|the\s+[\w:-]+(?:\s+[\w:-]+)?)\s+pass(?:es)?,?\s+I(?:'|’)ll\b`,
  ].join('|'),
  'i',
);

/** A sentence saying the branch went to the landing queue (and not that the submit failed). */
const SUBMITTED_RE = /\b(?:submitted|queued|handed)\b[^.\n]{0,80}\blanding\b|\blanding queue\b[^.\n]{0,60}\b(?:accepted|queued|has it)\b/i;
const SUBMIT_FAILED_RE = /\b(?:fail(?:s|ed)?|refused|rejected|could\s+not|couldn(?:'|’)t|cannot|blocked)\b/i;

/** The sentence of `text` around index `at`, trimmed to one plain line. */
function sentenceAt(text: string, at: number): string {
  const start = Math.max(text.lastIndexOf('. ', at) + 1, text.lastIndexOf('\n', at) + 1, 0);
  const ends = [text.indexOf('. ', at), text.indexOf('\n', at)].filter((i) => i >= 0);
  const end = ends.length ? Math.min(...ends) + 1 : text.length;
  const s = text.slice(start, end).replace(/[*_`#]+/g, '').replace(/\s+/g, ' ').trim();
  return s.length > 200 ? `${s.slice(0, 199)}…` : s;
}

/**
 * Judge a final report (ADR 0078). `criteria`: the item's acceptance criteria, in order (null when
 * not at hand: the verdict is then 'unmet' or 'unknown', never 'met'). `landed`: its branch landed,
 * which outweighs what the text says about landing; a criterion it reported failed still counts.
 */
/** A person ticked this criterion on the item (not the mission's own report): it counts as met. */
const personAccepted = (criteria: readonly (Pick<AcceptanceCriterion, 'id'> & Partial<Pick<AcceptanceCriterion, 'done' | 'checkedBy'>>)[] | null, id: string): boolean => {
  const c = criteria?.find((x) => x.id === id);
  return !!(c?.done && c.checkedBy && !c.checkedBy.startsWith('mission:'));
};

export function reportVerdict(report: Pick<MissionReport, 'text' | 'acceptance'>, criteria: readonly (Pick<AcceptanceCriterion, 'id'> & Partial<Pick<AcceptanceCriterion, 'done' | 'checkedBy'>>)[] | null, landed = false): MissionVerdictResult {
  // A criterion the report left unmet that a person accepted on the item is met (QA-105, as unmetCriteria).
  const failed = report.acceptance.filter((a) => !a.met && !personAccepted(criteria, a.id));
  if (failed.length) {
    const first = failed[0]!;
    const n = criteria ? criteria.findIndex((c) => c.id === first.id) + 1 : 0;
    const more = failed.length > 1 ? ` (and ${failed.length - 1} more)` : '';
    const evidence = first.evidence.replace(/\s+/g, ' ').trim();
    return { verdict: 'unmet', why: `Its report says ${n ? `AC-${n}` : 'a criterion'} is not met${more}${evidence ? `: ${evidence.length > 160 ? `${evidence.slice(0, 159)}…` : evidence}` : '.'}` };
  }
  if (!landed) {
    const m = UNFINISHED_RE.exec(report.text);
    if (m) return { verdict: 'unmet', why: `Its report says: "${sentenceAt(report.text, m.index)}"` };
    // The prompt asks for the report at the end of the final message: a session that stopped
    // mid-work ("Wiring the runner into the watch tick.") never wrote one.
    if (criteria?.length && !report.acceptance.length) {
      const last = sentenceAt(report.text, Math.max(0, report.text.trimEnd().lastIndexOf('. ', report.text.trimEnd().length - 2) + 2));
      return { verdict: 'unmet', why: `It wrote no acceptance report${last ? `; its last words were "${last}"` : ''}.` };
    }
  }
  if (criteria?.length && criteria.every((c) => personAccepted(criteria, c.id) || report.acceptance.some((a) => a.id === c.id && a.met))) return { verdict: 'met' };
  return { verdict: 'unknown' };
}

/** True when the report says its branch went to the landing queue (and the submit did not fail). */
export function reportSubmitted(text: string): boolean {
  const m = SUBMITTED_RE.exec(text);
  return !!m && !SUBMIT_FAILED_RE.test(sentenceAt(text, m.index));
}

/** POST /api/missions */
export interface MissionInput {
  projectId: Id;
  title: string;
  goal: string;
  kind?: MissionKind;
  pullRequest?: MissionPullRequest | null;
  itemId?: Id | null;
  /** ORGU-299: Watch incident this mission fixes or remedies, if any. */
  incidentId?: Id | null;
  owner?: string;
  budget?: number | null;
  /** Stop the mission when its spend passes the budget (default false). */
  budgetHardStop?: boolean;
  model?: string | null;
  placement?: { machineId?: Id; labels?: string[] };
}

/** PATCH /api/missions/:id. `closed: 'done' | 'stopped'` closes it, `null` reopens it. */
export interface MissionPatch {
  title?: string;
  goal?: string;
  kind?: MissionKind;
  pullRequest?: MissionPullRequest | null;
  budget?: number | null;
  budgetHardStop?: boolean;
  model?: string | null;
  itemId?: Id | null;
  closed?: 'done' | 'stopped' | null;
}

/** POST /api/missions/:id/start: runs session.start on the machine with a prompt built from the mission. */
export interface MissionStartRequest {
  machineId: Id;
  checkoutId?: Id;
  cwd?: string;
  newWorktree?: { branch: string };
  mode?: 'headless' | 'terminal';
  model?: string | null;
  permissionMode?: string;
  /** Extra instructions appended to the prompt. */
  note?: string;
  /** Sets the mission budget before starting. */
  budget?: number | null;
  /** Sets whether the mission stops when its spend passes the budget, before starting. */
  budgetHardStop?: boolean;
  /** The launch account the session runs on (ADR 0071); absent: the project's default, else none. */
  launchAccountId?: string;
  /** The agent kind to start (ADR 0049 decision 5); absent: Claude Code. Additive, 19 Oct 2026. */
  agentKind?: string;
  /** A thought level the kind advertises on that machine (ACP config category thought_level); else refused. Additive, 19 Oct 2026. */
  thoughtLevel?: string;
  /** Why the scheduler picked this kind (and model), recorded on the launch. Additive, 19 Oct 2026. */
  agentReason?: string;
}

export interface MissionStartResponse {
  ok: boolean;
  /** The machine did not answer in time: the mission waits for its session (launch.result 'pending'). */
  pending?: boolean;
  message?: string;
  mission: Mission;
  launch: MissionLaunch;
}

/** Where a start that is still running has got to. */
export type MissionStartStep = 'attachments' | 'worktree' | 'launching';

export const MISSION_START_STEP_LABEL: Record<MissionStartStep, string> = {
  attachments: 'Copying attachments',
  worktree: 'Creating worktree',
  launching: 'Launching session',
};

/** A start the hub is running now (cleared when the machine answers). */
export interface MissionStarting {
  missionId: Id;
  step: MissionStartStep;
  /** When it reached this step. */
  at: Ts;
}

/** state.ext.missions */
export interface MissionsState {
  items: BacklogItem[];
  missions: Mission[];
  /** Absent from older hubs: read with `?? []`. */
  links?: ItemLink[];
  keys?: Record<Id, ProjectKey>;
  views?: SavedView[];
  /** Open proposals in full, plus the 20 most recent closed ones. */
  proposals?: Proposal[];
  /** Open suggested defects, plus the 20 most recent decided ones. */
  signals?: DefectSignal[];
  sequences?: MissionSequence[];
  /** Each project's Autopilot that has settings (ADR 0073). Absent from older hubs. */
  autopilot?: AutopilotStatus[];
  /**
   * Who reviews each project's finished items (ORGU-85): its Autopilot setting, else Orgus when
   * its Autonomy is full, else people. Projects with items only. Absent from older hubs: people.
   */
  review?: Record<Id, ItemReviewMode>;
  /** Starts running now, with their step (never stored). */
  starting?: MissionStarting[];
  /** Every mission is paused (ADR 0103): nothing new starts, queued work waits. Absent or null: not paused. */
  pause?: MissionPause | null;
  /**
   * How many missions ended, by outcome, before folding (ADR 0103): `missions` carries each run of
   * repeats as one mission with `repeats`, and at most ENDED_PUBLISHED ended ones. Absent from older hubs.
   */
  ended?: { failed: number; stopped: number; done: number; published: number };
}

/** What other hub modules may ask: ctx.ext<MissionsApi>('missions'). */
export interface MissionsApi {
  /** The mission that started this session, if any (for cost and approval attribution). */
  missionOf(sessionId: Id): Id | null;
  mission(id: Id): Mission | undefined;
  /** A backlog item (optional: older hubs and the control plane may not offer it). */
  item?(id: Id): BacklogItem | undefined;
}

// ---------------------------------------------------------------------------
// Canonical session state
// ---------------------------------------------------------------------------

/**
 * The canonical session state machine every agent adapter maps onto
 * (starting, working, waiting_for_human, idle, then done, failed or stopped).
 */
export type CanonicalSessionState = 'starting' | 'working' | 'waiting_for_human' | 'idle' | 'done' | 'failed' | 'stopped';
export const CANONICAL_STATES: readonly CanonicalSessionState[] = ['starting', 'working', 'waiting_for_human', 'idle', 'done', 'failed', 'stopped'];
export const CANONICAL_STATE_LABEL: Record<CanonicalSessionState, string> = {
  starting: 'Starting',
  working: 'Working',
  waiting_for_human: 'Waiting for you',
  idle: 'Idle',
  done: 'Done',
  failed: 'Failed',
  stopped: 'Stopped',
};

/** What a caller may know beyond the session itself. */
export interface CanonicalHint {
  /** Orgus stopped it (session.stop). */
  stopped?: boolean;
  /** Its run failed (a launch that errored, a non-zero exit). */
  failed?: boolean;
}

/**
 * Map today's Claude Code session status onto the canonical state machine.
 *   working -> working, waiting -> waiting_for_human, idle -> idle,
 *   ended -> done (stopped or failed when the hint says so).
 * A live session that has not finished its first turn yet is starting.
 */
export function canonicalState(session: Pick<Session, 'status' | 'turns' | 'toolCalls' | 'lastText'>, hint: CanonicalHint = {}): CanonicalSessionState {
  switch (session.status) {
    case 'ended':
      return hint.failed ? 'failed' : hint.stopped ? 'stopped' : 'done';
    case 'waiting':
      return 'waiting_for_human';
    case 'working':
      return session.turns === 0 && session.toolCalls === 0 && !session.lastText ? 'starting' : 'working';
    case 'idle':
      return session.turns === 0 && session.toolCalls === 0 && !session.lastText ? 'starting' : 'idle';
  }
}

/**
 * A session whose last message is an error Claude Code printed instead of an answer (a model
 * it cannot use, an API error, no credit): it ended, but it failed.
 */
export const SESSION_ERROR_RE = /^\s*(?:API Error\b|Error:|Invalid API key|Credit balance is too low|Prompt is too long|Request timed out|Claude Code (?:could not|exited)|Not logged in|Please run \/login|OAuth token (?:has )?(?:expired|been revoked)|Claude AI usage limit reached|(?:\d+-hour|Session|Weekly|Opus|Usage) limit reached|You have hit your session limit\b|You(?:'|’)ve (?:hit|reached) your (?:(?:usage|session|weekly) )?limit|The model \S+ is not available\b)|There(?:'|’)s an issue with the selected model|issue with the selected model|model (?:\S+ )?(?:was )?not found/i;

/** An ended session failed: its last message is an error, or its agent classified a failure (ext.failure, ADR 0059). */
export function sessionFailed(s: Pick<Session, 'status' | 'lastText'> & Partial<Pick<Session, 'ext'>>): boolean {
  if (s.status !== 'ended') return false;
  return (!!s.lastText && SESSION_ERROR_RE.test(s.lastText)) || !!(s.ext?.failure && typeof s.ext.failure === 'object');
}

/** The error Claude Code prints when the account cannot use the model it was started with. */
export const MODEL_ERROR_RE = /issue with the selected model|model (?:\S+ )?(?:was )?not found|model \S+ is not available\b|model_not_found|(?:invalid|unknown) model/i;

/** True when a session's last message says its model is not available to the account. */
export function failedOnModel(s: Pick<Session, 'lastText'>): boolean {
  return !!s.lastText && MODEL_ERROR_RE.test(s.lastText);
}

/**
 * Why a failed session failed, in one sentence a person can act on: a model the account cannot
 * use names the model and the aliases that always work; anything else quotes the error.
 */
export function sessionFailureText(s: Pick<Session, 'lastText' | 'model'>, launchModel?: string | null): string {
  const model = launchModel ?? s.model;
  if (failedOnModel(s)) {
    return `The model ${model ? `${model} ` : ''}is not available to this account. Retry with opus, sonnet or haiku.`;
  }
  const text = s.lastText.replace(/\s+/g, ' ').trim();
  return text ? `The session failed: ${text.length > 200 ? `${text.slice(0, 199)}…` : text}` : 'The session failed. Open it to see why.';
}

export function isFinished(s: CanonicalSessionState): boolean {
  return s === 'done' || s === 'failed' || s === 'stopped';
}

// ---------------------------------------------------------------------------
// Prompt
// ---------------------------------------------------------------------------

/** The marker every mission prompt starts with; the session title (its first prompt) carries it. */
export function missionMarker(missionId: Id): string {
  return `[${missionId}]`;
}

/** A link as the prompt lists it: "Blocked by QAWL-3: Sign-in (In progress)". */
export interface PromptLink {
  label: string;
  key: string;
  title: string;
  status: string;
}

export type PromptItem = Pick<BacklogItem, 'id' | 'kind' | 'title' | 'body'> & Partial<Pick<BacklogItem, 'key' | 'acceptance' | 'defect' | 'labels' | 'components'>>;

/** One acceptance criterion on one line: "Given a, when b, then c" or the checklist text. */
export function criterionText(c: Pick<AcceptanceCriterion, 'format' | 'text' | 'given' | 'when' | 'then'>): string {
  if (c.format === 'gwt' && (c.given || c.when || c.then)) {
    const parts = [c.given && `Given ${c.given.trim()}`, c.when && `when ${c.when.trim()}`, c.then && `then ${c.then.trim()}`].filter(Boolean);
    return parts.join(', ');
  }
  return c.text.trim();
}

/** The mission a session works for, from the marker its title (its first prompt) starts with, or null. */
export function missionIdInTitle(title: string | null | undefined): Id | null {
  return /\[(msn_[0-9A-Za-z]+)\]/.exec(title ?? '')?.[1] ?? null;
}

/**
 * Where proof screenshots go and how to sign a browser in for them (QA-75). Images under
 * .orgus-dev/shots/ are kept on the mission when Orgus removes the worktree; the sign-in is the
 * one its own test hub prints, never the person's hub or their Orgus home.
 */
export const MISSION_PROOF_LINE =
  'Proof screenshots: save PNG, JPEG, GIF or WebP files under .orgus-dev/shots/ in this worktree (at most 12, 2 MB each); Orgus keeps them on the mission when it removes the worktree. To screenshot the UI, start your own test hub with ORGUS_HOME set to a new folder under .orgus-dev/homes/ and `orgus up --port <a free port other than 4600>`: it prints a one-time Sign in link in its own output, which you may use for that hub only. Never sign in to the person\'s hub, never read their Orgus home, and stop the test hub when you are done.';

/** How a mission should touch files: with the file tools, which its permission mode governs, not shell scripts. */
export const MISSION_TOOLS_LINE =
  'Read files with the Read tool and change them with Edit or Write. Shell commands that write files or run inline code (python -, node -e) may be refused or wait for a person.';

/**
 * ORGU-81: a mission decides for itself. The owner asked not to be asked, so a mission that ends
 * its turn on a question stalls until someone reads it.
 */
export const MISSION_DECIDE_LINE = 'Decide and continue: never end your turn on a question. Where a choice is yours, make the sensible one, say what you chose and why, and go on.';

/**
 * QA-62: a step the agent must not take itself (publishing, pushing, anything public or paid) is
 * left for a person on a line Orgus reads (parsePersonSteps), so it is never lost when the rest lands.
 */
export const MISSION_PERSON_LINE = 'If a step needs a person (publishing, pushing, a payment, anything public or outside this project), do the rest, then end your final message with one line per such step: "NEEDS A PERSON: <what and why>".';

/**
 * ADR 0086: a worktree mission's Work ends with commits main lacks and a clean worktree, so the
 * prompt asks for the commit (QA, 28 Oct 2026: a plain repo's mission left its edits uncommitted
 * and sat at Needs you).
 */
export const MISSION_WORKTREE_LINE =
  'Work in this folder: it is a worktree on its own branch. When the goal is met, commit your work on this branch so the worktree is clean (do not merge or push), then say so plainly and summarise what changed.';

/**
 * QA-78: a mission started vite on 4687 (inside 4600 to 4899, the hubs people look at) and left it
 * running. Orgus also stops what the session leaves listening (module 'dev-servers').
 */
export const MISSION_PORTS_LINE = 'Start dev servers only on a port from `orgus work port --for <what>` (above 4900) or the project\'s documented way, and stop every server you start before you finish.';

/** How the mission runs: `worktree` when it has a worktree and branch of its own. */
export interface MissionPromptOptions {
  worktree?: boolean;
  /** The project has a landing gate (hasLandingGate): the prompt says not to run the full gate locally. */
  landingGate?: boolean;
  /** Orgus's integrator lands the branch (a headless start in a worktree): commit and stop, never orgus land. */
  integrated?: boolean;
}

/**
 * ORGU-274: every mission was submitted twice, by its session and by the integrator, about 20 s
 * apart. The integrator is the only submitter; the session commits and stops.
 */
export const MISSION_INTEGRATED_LINE =
  'Orgus lands this branch itself once you stop: commit your work and end your turn. Do not run orgus land or submit the branch to a landing queue.';

/** The line the prompt asks a mission to end with, and the report parser reads. */
export const ACCEPTANCE_REPORT_HEADING = 'ACCEPTANCE REPORT';

/**
 * QA-98: the criteria the mission's report says are unmet and no person has accepted since (a
 * criterion checked done by someone other than a mission). Empty: the branch may land.
 */
export function unmetCriteria(report: Pick<MissionReport, 'acceptance' | 'blocked'> | null | undefined, criteria: readonly Pick<AcceptanceCriterion, 'id' | 'done' | 'checkedBy'>[]): string[] {
  if (!report || report.blocked) return [];
  return report.acceptance
    .filter((a) => !a.met)
    .filter((a) => {
      const c = criteria.find((x) => x.id === a.id);
      return !(c?.done && c.checkedBy && !c.checkedBy.startsWith('mission:'));
    })
    .map((a) => a.id);
}

/**
 * QA-98: whether the landing queue may land a branch a mission owns. The integrator is the single
 * submitter (a mission's own session never submits its branch), and it lands only when the report
 * has no unmet criterion a person has not accepted.
 */
export function missionAdmits(by: 'integrator' | 'session', unmet: readonly string[]): { ok: true } | { ok: false; reason: string } {
  if (by !== 'integrator') return { ok: false, reason: "A mission's own session never submits its branch: Orgus's integrator lands it once the mission is done." };
  if (unmet.length) return { ok: false, reason: `The mission's report has unmet criteria (${unmet.join(', ')}): it lands only once a person accepts them.` };
  return { ok: true };
}

/**
 * The prompt of a follow-up agent Orgus starts when integrating a mission's branch stopped on a
 * setback an agent can fix (QA-58): the marker and title, what happened, and the task.
 */
export function buildIntegrationFollowUpPrompt(
  mission: Pick<Mission, 'id' | 'title'>,
  f: { kind: FollowUpKind; branch: string; main?: string | null; conflicts?: readonly string[]; output?: string | null; message?: string | null },
): string {
  const main = f.main?.trim() || 'main';
  const parts = [`${missionMarker(mission.id)} ${mission.title.trim()}`, ''];
  if (f.kind === 'conflict') {
    const files = (f.conflicts ?? []).slice(0, 20);
    parts.push(`Orgus tried to merge ${main} into ${f.branch} to land this mission's work, and the merge stopped on conflicts${files.length ? ` in: ${files.join(', ')}` : ''}. Orgus aborted that merge.`);
    parts.push('', `Task: merge ${main} into this branch, resolve every conflict keeping the intent of both sides, run the tests the conflicting files touch and the type check, and commit the merge. Do not push.`);
  } else {
    parts.push(`Orgus merged ${main} into ${f.branch} to land this mission's work, and the project's checks failed.${f.message?.trim() ? ` ${clipText(f.message.trim(), 300)}` : ''}`);
    const out = f.output?.trim();
    if (out) parts.push('', 'The end of the output:', '```', out.length > 2000 ? `…${out.slice(-1999)}` : out, '```');
    parts.push('', `Task: merge ${main} into this branch if it is not in yet, fix what makes the checks fail, run the failing tests and the type check, and commit. Do not push.`);
  }
  parts.push('Orgus lands the branch after your commit and runs the full checks then, so do not run the whole suite yourself.', '', MISSION_TOOLS_LINE, MISSION_DECIDE_LINE, MISSION_PERSON_LINE);
  return parts.join('\n');
}

const clipText = (t: string, max: number) => (t.length > max ? `${t.slice(0, max - 1)}…` : t);

/**
 * The prompt a 'Start mission' sends: the marker and title on the first line (so the session's
 * title links it back), then the goal, the item with its acceptance criteria, defect details and
 * links, any extra note, and how to report back on each criterion.
 */
export function buildMissionPrompt(
  mission: Pick<Mission, 'id' | 'title' | 'goal'> & Partial<Pick<Mission, 'kind' | 'pullRequest'>>,
  item?: PromptItem | null,
  note?: string,
  links: readonly PromptLink[] = [],
  opts: MissionPromptOptions = {},
): string {
  const parts = [`${missionMarker(mission.id)} ${mission.title.trim()}`, '', `Goal: ${mission.goal.trim()}`];
  if (mission.kind === 'review' && mission.pullRequest) {
    const pr = mission.pullRequest;
    parts.push('', `Pull Request #${pr.number}: ${pr.title}`);
    parts.push(`Repository: ${pr.repo}`);
    parts.push(`Head branch: ${pr.branch} (${pr.headSha})`);
    if (pr.baseBranch) parts.push(`Base branch: ${pr.baseBranch}`);
    if (pr.url) parts.push(`URL: ${pr.url}`);
    parts.push('', 'Review Instructions:');
    parts.push('- Review the changes in this pull request thoroughly and constructively.');
    parts.push('- Point out potential bugs, edge cases, performance issues, or architectural concerns.');
    parts.push('- CRITICAL: Never approve or merge the pull request. This review must only provide comments.');
  }
  const criteria = (item?.acceptance ?? []).filter((c) => criterionText(c));
  if (item) {
    parts.push('', `${ITEM_KIND_LABEL[item.kind]} ${item.key ? `${item.key} ` : ''}(${item.id}): ${item.title.trim()}`);
    if (item.body.trim()) parts.push(item.body.trim());
    const d = item.defect;
    if (d) {
      const lines = [`Severity: ${SEVERITY_LABEL[d.severity] ?? d.severity}`];
      if (d.environment.trim()) lines.push(`Environment: ${d.environment.trim()}`);
      if (d.steps.trim()) lines.push(`Steps to reproduce:\n${d.steps.trim()}`);
      if (d.expected.trim()) lines.push(`Expected: ${d.expected.trim()}`);
      if (d.actual.trim()) lines.push(`Actual: ${d.actual.trim()}`);
      parts.push('', 'Defect details:', ...lines);
    }
    if (criteria.length) {
      parts.push('', 'Acceptance criteria:');
      criteria.forEach((c, i) => parts.push(`AC-${i + 1}: ${criterionText(c)}`));
    }
  }
  if (links.length) {
    parts.push('', 'Related items:');
    for (const l of links) parts.push(`- ${l.label} ${l.key}: ${l.title.trim()} (${l.status})`);
  }
  if (note?.trim()) parts.push('', note.trim());
  parts.push('', opts.worktree ? MISSION_WORKTREE_LINE : 'Work in this folder. When the goal is met, say so plainly and summarise what changed.');
  if (opts.worktree) parts.push(MISSION_PROOF_LINE);
  if (opts.integrated) parts.push(MISSION_INTEGRATED_LINE);
  if (opts.landingGate) parts.push(LANDING_GATE_LINE);
  // Dogfood, 18 Oct 2026: a mission read files with cat and changed them with python heredocs, so
  // every edit waited for a person as inline code while the Edit tool would have run at once.
  parts.push(MISSION_TOOLS_LINE, MISSION_PORTS_LINE, MISSION_DECIDE_LINE, MISSION_PERSON_LINE);
  if (criteria.length) {
    parts.push(
      `End your final message with a section headed ${ACCEPTANCE_REPORT_HEADING}, one line per criterion, for example:`,
      ...criteria.map((_, i) => `AC-${i + 1}: PASS - what shows it is met (or FAIL - why not)`),
    );
  }
  return parts.join('\n');
}

/** True when a session's title (its first prompt) carries this mission's marker. */
export function sessionCarriesMission(session: Pick<Session, 'title'>, missionId: Id): boolean {
  return session.title.includes(missionMarker(missionId));
}

/** A folder path compared loosely: slashes unified, case folded, no trailing slash. */
function samePath(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  const n = (p: string) => trimEndOf(p.replace(/\\/g, '/'), '/').toLowerCase();
  return n(a) === n(b);
}

/** How long after a launch its session may first appear and still be matched by folder. */
const MATCH_WINDOW_MS = 10 * 60_000;

/**
 * Launches whose session id is not known yet (terminal starts, and starts the machine did not
 * answer in time), each matched to a session on the launch's machine that started after it:
 * first one whose title carries the mission marker (its first prompt), else the only new
 * session in the launch's folder.
 * `claimed` holds session ids other missions already own. Returns launch id -> session id.
 */
/**
 * Sessions by machine, oldest first, remembered per list: the console derives every mission against the same
 * session list on each change, so the list is sorted and grouped once, not scanned once per
 * mission with a pending launch (2,000 sessions times a hundred missions on every render). Entries are never changed in
 * place by any caller, so a remembered order cannot go stale while the list is the same object.
 */
const sortedLists = new WeakMap<object, Map<Id, Session[]>>();
/** Each machine's sessions oldest first (see above). */
function sortedByMachine(sessions: Iterable<Session>): Map<Id, Session[]> {
  const key = typeof sessions === 'object' && sessions !== null ? sessions : null;
  const hit = key ? sortedLists.get(key) : undefined;
  if (hit) return hit;
  const out = new Map<Id, Session[]>();
  for (const s of [...sessions].sort((a, b) => a.startedAt - b.startedAt)) {
    const list = out.get(s.machineId);
    if (list) list.push(s);
    else out.set(s.machineId, [s]);
  }
  if (key && Array.isArray(sessions)) sortedLists.set(key, out);
  return out;
}

export function matchPendingLaunches(
  mission: Pick<Mission, 'id' | 'launches' | 'sessionIds'>,
  sessions: Iterable<Session>,
  claimed: ReadonlySet<Id> = new Set(),
): Map<Id, Id> {
  const out = new Map<Id, Id>();
  const pending = mission.launches.filter((l) => l.result !== 'error' && !l.sessionId);
  if (!pending.length) return out;
  const taken = new Set([...mission.sessionIds, ...claimed]);
  const byMachine = sortedByMachine(sessions);
  for (const l of [...pending].sort((a, b) => a.at - b.at)) {
    const fresh = (byMachine.get(l.machineId) ?? []).filter((c) => !taken.has(c.id) && c.startedAt >= l.at - 60_000);
    let s = fresh.find((c) => sessionCarriesMission(c, mission.id));
    if (!s) {
      const inFolder = fresh.filter((c) => c.startedAt <= l.at + MATCH_WINDOW_MS && (samePath(c.cwd, l.worktree) || samePath(c.cwd, l.cwd)));
      if (inFolder.length === 1) s = inFolder[0];
    }
    if (!s) continue;
    taken.add(s.id);
    out.set(l.id, s.id);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Derived mission view
// ---------------------------------------------------------------------------

/**
 * 'unmet': its newest session ended without finishing, by its own report (ADR 0078).
 * 'interrupted': its newest session stopped before it finished (its process ended), not by a person.
 */
export type BlockerKind = 'waiting' | 'approval' | 'idle' | 'offline' | 'launch_failed' | 'session_failed' | 'unseen' | 'budget' | 'unmet' | 'interrupted';

export interface Blocker {
  kind: BlockerKind;
  /** One plain sentence. */
  title: string;
  since: Ts;
  sessionId?: Id;
  machineId?: Id;
  approvalId?: Id;
  /** kind 'approval' only: set when a decided request stopped the session (R-HL-3); absent for a pending one. */
  outcome?: ApprovalOutcome;
  /** Approve and resume is offered (expired, or denied by a person). */
  resumable?: boolean;
  phase?: ApprovalBlock['phase'];
  /** kind 'approval' with an outcome: the primary action (approvalBlockLead). */
  lead?: ApprovalBlock['lead'];
  /** A person denied it with a note telling the session what to do instead. */
  redirect?: ApprovalBlock['redirect'];
  /** What was asked, and when: for "Blocked: approval expired" with what and when. */
  asked?: { title: string; tool?: string; subject?: string; at: Ts; decidedAt: Ts; by: string };
}

export interface MissionSessionView {
  /** The session id, or the launch id while the session has not been seen yet. */
  id: Id;
  sessionId: Id | null;
  machineId: Id;
  state: CanonicalSessionState;
  session: Session | null;
  launch: MissionLaunch | null;
  /** Set when a decided approval (expired or denied) stopped this session (approvalBlockFor). */
  block?: ApprovalBlock;
}

export interface MissionSpend {
  /** USD, when the costs module reports it; null otherwise. */
  cost: number | null;
  tokens: { input: number; output: number; cacheRead: number };
  /** Some of its sessions ran on a model with no price (another vendor's): cost leaves them out. */
  unpriced?: boolean;
}

export interface MissionView {
  state: MissionState;
  /** 0..1. */
  progress: number;
  /** One line under the progress bar: "1 of 3 sessions finished, 2 working". */
  progressLabel: string;
  sessions: MissionSessionView[];
  counts: Record<CanonicalSessionState, number>;
  blockers: Blocker[];
  pendingApprovals: ApprovalRequest[];
  spend: MissionSpend;
  /** spend.cost / budget, when both are known. */
  budgetUsed: number | null;
  /**
   * The newest finished session whose final report has not been read yet (ADR 0078): the hub
   * reads it, and the mission is not done until it has. Null when there is nothing to read.
   */
  reportDue: { sessionId: Id; activity: Ts } | null;
  /** What its final report says (reportVerdict without the criteria), once read and current; else null. */
  verdict: MissionVerdictResult | null;
  /**
   * QA-66: the next step is Orgus's, not a person's: 'integrate' while the idle session Orgus
   * launched waits for Orgus to stop it and integrate its branch (ADR 0086 phase 1). Absent otherwise.
   */
  orgusNext?: 'integrate';
}

export interface DeriveContext {
  sessions: (id: Id) => Session | undefined;
  /** Every session, for linking launches whose session id is not known yet. */
  allSessions?: Iterable<Session>;
  /**
   * Pending and blocking requests (approvalsForDerive); narrower objects with just the core
   * fields fit. A decided request that stopped a session (expired and parked, or denied) means
   * that session stopped there, it did not finish.
   */
  approvals?: ApprovalLike[] | null;
  costs?: Pick<CostsState, 'sessions'> | null;
  machineOnline?: (machineId: Id) => boolean;
  now: number;
  /** A launch not seen as a session after this long becomes a blocker. Default 3 minutes. */
  unseenAfterMs?: number;
  /** The mission's backlog item, so its final report is judged against the item's criteria (ADR 0078). */
  item?: (id: Id) => Pick<BacklogItem, 'acceptance'> | undefined;
  /**
   * The hub reads final reports (ADR 0078): a mission whose newest session finished is not done
   * until its report has been read, for at most REPORT_WAIT_MS. Contexts that never read reports
   * (the control plane) leave it unset.
   */
  readsReports?: boolean;
}

/** A launched session that never shows up is assumed finished after this long. */
const FORGOTTEN_AFTER_MS = 24 * 60 * 60_000;
/**
 * A headless start whose session id is known but never reported fails after this long (ADR 0078):
 * Orgus cannot follow it, and it held an Autopilot slot for a day (ORGU-95, 19 Oct 2026).
 */
export const UNSEEN_FAIL_MS = 30 * 60_000;
/** How long a finished mission waits for its final report to be read before it is judged without it. */
export const REPORT_WAIT_MS = 10 * 60_000;
/** How long a mission whose report says its branch went to the landing queue waits for it to land. */
export const LANDING_WAIT_MS = 45 * 60_000;

/** Weight of each canonical state in the progress bar. */
const PROGRESS_WEIGHT: Record<CanonicalSessionState, number> = {
  starting: 0.1,
  working: 0.5,
  waiting_for_human: 0.5,
  idle: 0.75,
  done: 1,
  failed: 1,
  stopped: 1,
};

function emptyCounts(): Record<CanonicalSessionState, number> {
  return { starting: 0, working: 0, waiting_for_human: 0, idle: 0, done: 0, failed: 0, stopped: 0 };
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The blocker for a session a decided approval stopped. */
function blockerOf(b: ApprovalBlock, s: Session): Blocker {
  const a = b.approval;
  const decidedAt = a.decidedAt ?? a.createdAt;
  return {
    kind: 'approval',
    title: b.title,
    since: decidedAt,
    sessionId: s.id,
    machineId: s.machineId,
    approvalId: a.id,
    outcome: b.outcome,
    resumable: b.resumable,
    phase: b.phase,
    lead: b.lead,
    ...(b.redirect ? { redirect: b.redirect } : {}),
    asked: { title: a.title, ...(a.tool ? { tool: a.tool } : {}), ...(a.subject ? { subject: a.subject } : {}), at: a.createdAt, decidedAt, by: a.decidedBy ?? 'timeout' },
  };
}

/**
 * Everything the console shows about a mission, from its launches and live sessions:
 *   state     closed wins; else waiting (a session or approval needs a person, or every live
 *             session is idle, unless its branch landed), running (something is starting or
 *             working, or idle after its branch landed), failed (only failed
 *             launches), done or stopped (everything finished), planned (nothing launched).
 *   progress  the mean of each session's weight (starting 0.1, working 0.5, idle 0.75,
 *             finished 1); closed as done is 1; planned is 0.
 */
/**
 * Did Orgus stop this session itself to integrate its launch's finished work (ADR 0086 phase 1)?
 * The integration record names the session it stopped; a record from before the field existed is
 * read from its step.
 */
export function stoppedByIntegrator(mission: Pick<Mission, 'integration'>, launch: Pick<MissionLaunch, 'id'> | null, sessionId: Id): boolean {
  const rec = mission.integration;
  if (!rec || !launch || rec.launchId !== launch.id) return false;
  if (rec.stoppedSessionId) return rec.stoppedSessionId === sessionId;
  return rec.steps.some((st) => st.what.startsWith('Stopped the idle session'));
}

export function deriveMission(mission: Mission, ctx: DeriveContext): MissionView {
  const counts = emptyCounts();
  const sessions: MissionSessionView[] = [];
  const blockers: Blocker[] = [];
  const seen = new Set<Id>();
  const unseenAfter = ctx.unseenAfterMs ?? 3 * 60_000;
  const matched = ctx.allSessions ? matchPendingLaunches(mission, ctx.allSessions) : new Map<Id, Id>();
  const stopped = mission.closed?.as === 'stopped';
  let failedStarts = 0;

  const add = (sessionId: Id, launch: MissionLaunch | null) => {
    if (seen.has(sessionId)) return;
    seen.add(sessionId);
    const s = ctx.sessions(sessionId) ?? null;
    // An expired or denied approval that stopped the session never reads as done (R-HL-3).
    const block = s && !mission.closed ? approvalBlockFor(s, ctx.approvals ?? [], ctx.now) : null;
    let state: CanonicalSessionState;
    // The adapter's own reading counts too: a non-zero exit is failed, a session Orgus stopped is stopped.
    // QA-53: a session Orgus stopped to land the work finished it, even after a hub restart, when
    // the machine reports the stopped process as crashed or failed.
    if (s && s.status === 'ended' && !block && stoppedByIntegrator(mission, launch, s.id)) state = 'done';
    else if (s)
      state = block
        ? block.phase === 'resuming'
          ? 'working'
          : 'waiting_for_human'
        : canonicalState(s, { stopped: (stopped && s.status === 'ended') || (s.status === 'ended' && s.state === 'stopped'), failed: sessionFailed(s) || (s.status === 'ended' && s.state === 'failed') });
    else if (launch && !mission.closed && launch.mode === 'headless' && ctx.now - launch.at >= UNSEEN_FAIL_MS && ctx.now - launch.at < FORGOTTEN_AFTER_MS) {
      // Never reported in half an hour: Orgus cannot follow it, so it does not hold the mission open.
      state = 'failed';
      blockers.push({
        kind: 'unseen',
        title: `The session never reported to Orgus within ${Math.round(UNSEEN_FAIL_MS / 60_000)} minutes, so Orgus cannot follow it. Check the machine, then retry the mission.`,
        since: launch.at,
        machineId: launch.machineId,
        sessionId,
      });
    } else if (launch && !mission.closed && ctx.now - launch.at < FORGOTTEN_AFTER_MS) {
      // Known id, not reported yet: the machine has not sent it, or it is still warming up.
      state = 'starting';
      if (ctx.now - launch.at > unseenAfter) {
        blockers.push({ kind: 'unseen', title: 'A session was started but has not been reported yet. Check the machine.', since: launch.at, machineId: launch.machineId, sessionId });
      }
    } else state = stopped ? 'stopped' : 'done'; // Aged out of the machine's 7-day window, or its machine was removed.
    counts[state]++;
    sessions.push({ id: sessionId, sessionId, machineId: s?.machineId ?? launch?.machineId ?? '', state, session: s, launch, ...(block ? { block } : {}) });
  };

  for (const l of mission.launches) {
    const sid = l.sessionId ?? matched.get(l.id) ?? null;
    if (sid) {
      add(sid, l);
      continue;
    }
    if (l.result === 'error') {
      // A start the machine refused: shown, but it is not a session and does not move progress.
      failedStarts++;
      sessions.push({ id: l.id, sessionId: null, machineId: l.machineId, state: 'failed', session: null, launch: l });
      const later = mission.launches.some((x) => x.result !== 'error' && x.at >= l.at);
      if (!later && !mission.closed) {
        blockers.push({ kind: 'launch_failed', title: `The last start failed: ${l.message ?? 'the machine did not say why.'}`, since: l.at, machineId: l.machineId });
      }
      continue;
    }
    // Started, session not reported yet. A terminal launch, a headless one still warming up, or
    // a start the machine did not answer in time ('pending'): never failed until it says so.
    const state: CanonicalSessionState = stopped ? 'stopped' : 'starting';
    counts[state]++;
    sessions.push({ id: l.id, sessionId: null, machineId: l.machineId, state, session: null, launch: l });
    if (!stopped && ctx.now - l.at > unseenAfter) {
      blockers.push({
        kind: 'unseen',
        title:
          l.result === 'pending'
            ? 'The machine did not answer the start in time, and no session has been reported yet. Check the machine.'
            : 'A session was started but has not been reported yet. Check the machine, or the terminal it opened.',
        since: l.at,
        machineId: l.machineId,
      });
    }
  }
  for (const id of mission.sessionIds) add(id, null);

  // A session that ended at a request nobody answered in time (parked), or that a person denied
  // right before it ended, stopped there: it did not finish the work.
  const parkedAt = new Map<Id, true>();
  if (!mission.closed) {
    for (const v of sessions) {
      const s = v.session;
      if (!s || v.state !== 'done' || s.status !== 'ended') continue;
      // approvalBlockFor already holds a blocked session at waiting_for_human (R-HL-3); this is
      // the safety net for a parked request it does not hold (one still pending beside it, say),
      // unless later work or Mark as handled cleared it.
      const park = (ctx.approvals ?? []).find(
        (a) => a.parked && a.state === 'expired' && a.sessionId === s.id && a.createdAt >= s.startedAt && (!a.machineId || a.machineId === s.machineId) && !a.laterWorkAt && !a.dismissedAt && a.decidedBy !== 'session',
      );
      if (!park) continue;
      parkedAt.set(s.id, true);
      counts.done--;
      counts.stopped++;
      v.state = 'stopped';
      blockers.push({
        kind: 'approval',
        title: `Timed out: nobody answered "${park.title}" in time, so it was denied and the session stopped. Retry the mission to go on.`,
        since: park.decidedAt ?? park.createdAt,
        sessionId: s.id,
        machineId: s.machineId,
      });
    }
  }

  // A session that failed says why, with the fix when it is the model, unless a later one started.
  if (!mission.closed) {
    for (const v of sessions) {
      const s = v.session;
      if (!s || v.state !== 'failed') continue;
      if (sessions.some((o) => o !== v && o.state !== 'failed' && (o.session?.startedAt ?? o.launch?.at ?? 0) > s.startedAt)) continue;
      blockers.push({ kind: 'session_failed', title: sessionFailureText(s, v.launch?.model), since: s.lastActivity, sessionId: s.id, machineId: s.machineId });
    }
  }

  for (const v of sessions) {
    const s = v.session;
    if (!s) continue;
    if (v.block && v.block.phase !== 'resuming') blockers.push(blockerOf(v.block, s));
    else if (v.state === 'waiting_for_human') {
      // The group or pill already says Needs you: the title is what it asks, never "Waiting for you" again.
      blockers.push({ kind: 'waiting', title: s.lastText ? `Asks: ${s.lastText}` : 'A session needs your answer.', since: s.lastActivity, sessionId: s.id, machineId: s.machineId });
    }
    if (!isFinished(v.state) && ctx.machineOnline && !ctx.machineOnline(s.machineId)) {
      blockers.push({ kind: 'offline', title: 'The machine running a session is offline, so its state may be stale.', since: s.lastActivity, sessionId: s.id, machineId: s.machineId });
    }
  }

  const ids = new Set(sessions.map((s) => s.sessionId).filter((x): x is Id => !!x));
  // A request counts for a session only from the machine that runs it (another machine cannot name it).
  const machineOf = new Map(sessions.map((s) => [s.sessionId, s.machineId] as const));
  const ownSession = (a: ApprovalLike) => ids.has(a.sessionId) && (!machineOf.get(a.sessionId) || !a.machineId || machineOf.get(a.sessionId) === a.machineId);
  const pendingApprovals = (ctx.approvals ?? [])
    .filter((a) => a.state === 'pending' && (a.missionId === mission.id || ownSession(a)))
    .sort((a, b) => a.createdAt - b.createdAt) as ApprovalRequest[];
  for (const a of pendingApprovals) {
    blockers.push({ kind: 'approval', title: `Approval needed: ${a.title}`, since: a.createdAt, sessionId: a.sessionId, machineId: a.machineId, approvalId: a.id });
    // A session held at a request waits for a person, whatever its transcript says: the pill,
    // the caption and the session row all say so.
    const v = sessions.find((x) => x.sessionId === a.sessionId);
    if (v && (v.state === 'working' || v.state === 'starting' || v.state === 'idle')) {
      counts[v.state]--;
      counts.waiting_for_human++;
      v.state = 'waiting_for_human';
    }
  }

  // Spend: the costs module when it is on, else tokens from the transcripts.
  const tokens = { input: 0, output: 0, cacheRead: 0 };
  for (const v of sessions) {
    if (!v.session) continue;
    tokens.input += v.session.tokens.input;
    tokens.output += v.session.tokens.output;
    tokens.cacheRead += v.session.tokens.cacheRead;
  }
  let cost: number | null = null;
  let unpriced = false;
  if (ctx.costs) {
    cost = 0;
    for (const u of ctx.costs.sessions) {
      if (u.missionId !== mission.id && !ids.has(u.sessionId)) continue;
      cost += u.total.cost;
      if (u.unpriced) unpriced = true;
    }
  }
  const budgetUsed = cost != null && mission.budget != null && mission.budget > 0 ? cost / mission.budget : null;
  if (budgetUsed != null && budgetUsed >= 1 && !mission.closed) {
    // A warning, not a stop: sessions keep running, so the title says so while any still runs.
    const live = counts.starting + counts.working + counts.waiting_for_human + counts.idle > 0;
    blockers.push({ kind: 'budget', title: `Over budget: $${cost!.toFixed(2)} of $${mission.budget!.toFixed(2)} spent.${live ? ' Sessions keep running.' : ''}`, since: ctx.now });
  }

  const finished = counts.done + counts.failed + counts.stopped;
  const real = sessions.filter((s) => !(s.launch?.result === 'error' && !s.sessionId));
  const total = real.length;

  // The newest attempt decides how a finished mission ended (ADR 0078): a session that ended well
  // and a later one that failed is a failed mission, not a done one. A start the machine refused
  // after it is the newest attempt too.
  const attemptAt = (s: MissionSessionView) => s.launch?.at ?? s.session?.startedAt ?? 0;
  const newest = real.reduce<MissionSessionView | null>((a, s) => (!a || attemptAt(s) >= attemptAt(a) ? s : a), null);
  const lastStart = mission.launches.reduce<MissionLaunch | null>((a, l) => (!a || l.at >= a.at ? l : a), null);
  const startFailedLast = !!lastStart && lastStart.result === 'error' && (!newest || lastStart.at > attemptAt(newest));

  // Its final report, when it is the newest finished session's and current; else it is due.
  let reportDue: MissionView['reportDue'] = null;
  let verdict: MissionVerdictResult | null = null;
  // QA-56: a session Orgus stopped to land the work (ADR 0086) finished its turn too: its report is
  // read, and judged, before the work lands, so an unmet report never lands by itself.
  const stoppedToLand = newest?.state === 'stopped' && !!mission.integration && !!newest.launch && mission.integration.launchId === newest.launch.id && mission.closed?.as !== 'stopped';
  const ended = (newest?.state === 'done' || stoppedToLand) && newest?.session ? newest.session : null;
  if (ended) {
    const r = mission.report && !mission.report.blocked ? mission.report : null;
    // A later turn of the same session (a message after it finished) is read again.
    const current = !!r && (r.sessionId ? r.sessionId === ended.id && ended.lastActivity - (r.activity ?? 0) <= 5_000 : r.at >= ended.startedAt);
    // Dogfood G7: a follow-up agent Orgus started to fix a landing (QA-58) reports on that fix, not
    // the story's criteria (the first session did): no acceptance report there is not unfinished work.
    const criteria = newest?.launch?.followUp ? null : ((mission.itemId ? ctx.item?.(mission.itemId)?.acceptance.filter((c) => criterionText(c)) : null) ?? null);
    if (current) verdict = reportVerdict(r!, criteria, !!mission.landed);
    else if (mission.closed?.as !== 'stopped') reportDue = { sessionId: ended.id, activity: ended.lastActivity };
  }

  let state: MissionState;
  let wait: 'report' | 'landing' | 'integrate' | null = null;
  if (mission.closed) state = mission.closed.as;
  else if (!total) state = failedStarts ? 'failed' : 'planned';
  else if (counts.waiting_for_human > 0 || pendingApprovals.length > 0) state = 'waiting';
  else if (counts.starting + counts.working > 0) state = 'running';
  // Its branch landed and its input was closed: a finished turn is the session wrapping up.
  else if (counts.idle > 0 && mission.landed) state = 'running';
  // QA-66: Orgus stops this idle session and integrates its branch on its next pass: Orgus's step.
  else if (counts.idle > 0 && orgusIntegratesNext(mission, sessions, ctx.now)) {
    state = 'running';
    wait = 'integrate';
  } else if (counts.idle > 0 && submittedToLanding(mission)) {
    // QA-129: its branch is in the landing queue: Orgus lands it, no person's step (no Inbox item).
    state = 'running';
    wait = 'landing';
  } else if (counts.idle > 0) state = 'waiting';
  else if (startFailedLast || newest?.state === 'failed') state = 'failed';
  // QA-51: Orgus stopping the idle session it launched, to land the work (ADR 0086 phase 1), is
  // the end of the work stage, not a stopped mission.
  else if (newest?.state === 'stopped' && !(mission.integration && newest.launch && mission.integration.launchId === newest.launch.id)) state = 'stopped';
  else if (mission.landed) state = 'done';
  // QA-121: handed to the landing, but its worktree and branch vanished and the work is not on main.
  else if (mission.integration?.status === 'lost') {
    state = 'failed';
    blockers.push({ kind: 'unmet', title: mission.integration.message ?? LOST_NEXT_STEP, since: mission.integration.at });
  } else if (verdict?.verdict === 'unmet') {
    // Its branch went to the landing queue: the landing, not the text, says whether it finished.
    if (ended && reportSubmitted(mission.report!.text) && ctx.now - ended.lastActivity < LANDING_WAIT_MS) {
      state = 'running';
      wait = 'landing';
    } else {
      state = 'failed';
      blockers.push({ kind: 'unmet', title: `The session ended without finishing. ${verdict.why}`, since: ended?.lastActivity ?? ctx.now, sessionId: ended?.id, machineId: ended?.machineId });
    }
  } else if (reportDue && ctx.readsReports && ctx.now - reportDue.activity < REPORT_WAIT_MS) {
    // Not done until Orgus has read what it says about its work.
    state = 'running';
    wait = 'report';
  } else state = 'done';

  // A session that stopped on its own (its process ended) says so, so the stop is never unexplained.
  if (state === 'stopped' && !mission.closed && newest?.session && !parkedAt.has(newest.session.id) && !newest.block) {
    blockers.push({
      kind: 'interrupted',
      title: 'The session stopped before it finished: its process ended (a hub or machine restart, or a crash). Retry the mission to go on.',
      since: newest.session.lastActivity,
      sessionId: newest.session.id,
      machineId: newest.session.machineId,
    });
  }

  if (state === 'waiting' && counts.idle > 0 && counts.waiting_for_human === 0 && !pendingApprovals.length && sessions.some((s) => s.state === 'idle' && !parkedAt.has(s.sessionId ?? ''))) {
    const idle = sessions.find((s) => s.state === 'idle' && !parkedAt.has(s.sessionId ?? ''));
    blockers.push({
      kind: 'idle',
      title: 'A session finished its turn and is waiting for your next prompt, or for you to close the mission.',
      since: idle?.session?.lastActivity ?? ctx.now,
      sessionId: idle?.sessionId ?? undefined,
      machineId: idle?.machineId,
    });
  }

  let progress = 0;
  if (mission.closed?.as === 'done') progress = 1;
  // A session that stopped at a request, or ended without finishing, did not finish its work: it
  // never fills the bar (R-HL-3, ADR 0078).
  else if (total) {
    const unfinished = (s: MissionSessionView) => (s.sessionId && parkedAt.has(s.sessionId) && s.state === 'stopped') || (s === newest && state === 'failed' && s.state === 'done');
    progress = real.reduce((sum, s) => sum + (unfinished(s) ? 0.5 : PROGRESS_WEIGHT[s.state]), 0) / total;
  }

  let progressLabel: string;
  if (!total) progressLabel = mission.closed ? 'Closed before any session started' : failedStarts ? 'Not started: the start failed' : 'Not started';
  else {
    const bits = [`${finished} of ${plural(total, 'session')} finished`];
    if (counts.working + counts.starting) bits.push(`${counts.working + counts.starting} working`);
    const blocked = sessions.filter((s) => s.block && s.block.phase !== 'resuming').length;
    if (blocked) bits.push(`${blocked} blocked on an approval`);
    if (counts.waiting_for_human - blocked > 0) bits.push(`${counts.waiting_for_human - blocked} ${counts.waiting_for_human - blocked === 1 ? 'needs' : 'need'} you`);
    if (counts.idle) bits.push(`${counts.idle} idle`);
    if (counts.failed) bits.push(`${counts.failed} failed`);
    if (blockers.some((b) => b.kind === 'unmet')) bits.push('the last one ended unfinished');
    if (wait === 'report') bits.push('reading its final report');
    if (wait === 'landing') bits.push('waiting for its branch to land');
    progressLabel = wait === 'integrate' ? 'Orgus: merge main' : bits.join(', ');
  }

  blockers.sort((a, b) => a.since - b.since);
  return {
    state,
    progress: Math.max(0, Math.min(1, progress)),
    progressLabel,
    sessions,
    counts,
    blockers,
    pendingApprovals,
    spend: { cost, tokens, ...(unpriced ? { unpriced: true } : {}) },
    budgetUsed,
    reportDue,
    verdict,
    ...(wait === 'integrate' ? { orgusNext: 'integrate' as const } : {}),
  };
}

/** QA-129: the newest start's branch was handed to the landing queue and has not landed yet. */
export function submittedToLanding(mission: Pick<Mission, 'launches' | 'integration' | 'landed' | 'closed'>): boolean {
  if (mission.closed || mission.landed || mission.integration?.status !== 'submitted') return false;
  const lastStart = mission.launches.reduce<MissionLaunch | null>((a, l) => (!a || l.at >= a.at ? l : a), null);
  return !!lastStart && lastStart.id === mission.integration.launchId;
}

/**
 * QA-66: will Orgus integrate this mission's work on its next pass (integrateDecision in the hub)?
 * Its newest start is Orgus's own headless one on a branch, every idle session is that start's
 * Orgus-launched session, and no setback for that start waits for a person. The hub checks the
 * worktree too; the integrator's pass follows within a tick.
 */
function orgusIntegratesNext(mission: Mission, sessions: readonly MissionSessionView[], now: Ts): boolean {
  if (mission.paused || mission.landed) return false;
  const launch = newestLaunch(mission);
  if (!launch || launch.mode !== 'headless' || !launch.branch || !(launch.worktree ?? launch.cwd) || !launch.sessionId) return false;
  const rec = mission.integration?.launchId === launch.id ? mission.integration : null;
  if (!rec && now - launch.at > INTEGRATE_MAX_LAUNCH_AGE_MS) return false;
  if (rec && rec.status !== 'stopping' && !(rec.status === 'error' && rec.attempts < INTEGRATE_MAX_ATTEMPTS)) return false;
  const idle = sessions.filter((s) => s.state === 'idle');
  return idle.length > 0 && idle.every((s) => s.sessionId === launch.sessionId && s.session?.origin === 'orgus');
}

/** How far along one leaf item is, from its status alone. */
export function statusFraction(status: ItemStatus): number {
  return status === 'done' ? 1 : status === 'review' ? 0.8 : status === 'in_progress' ? 0.4 : 0;
}

type ProgressNode = Pick<BacklogItem, 'id' | 'status'> & Partial<Pick<BacklogItem, 'estimate' | 'parentId'>>;

/**
 * Progress of an item rolled up over everything under it (dropped items left out): each leaf
 * counts its estimate in points (1 when it has none), weighted by how far its status is.
 * `done` and `total` count the leaves; `points` sums the estimates.
 */
export function itemProgress(item: ProgressNode, items: readonly ProgressNode[]): { done: number; total: number; fraction: number; points: { done: number; total: number } } {
  const kids = new Map<Id, ProgressNode[]>();
  for (const i of items) {
    if (!i.parentId || i.status === 'dropped') continue;
    const list = kids.get(i.parentId) ?? [];
    list.push(i);
    kids.set(i.parentId, list);
  }
  let done = 0;
  let total = 0;
  let weight = 0;
  let got = 0;
  let pDone = 0;
  let pTotal = 0;
  const seen = new Set<Id>();
  const walk = (node: ProgressNode, depth: number) => {
    if (seen.has(node.id) || depth > 50) return;
    seen.add(node.id);
    const children = kids.get(node.id) ?? [];
    if (!children.length) {
      const w = node.estimate && node.estimate > 0 ? node.estimate : 1;
      total++;
      weight += w;
      got += w * statusFraction(node.status);
      if (node.status === 'done') done++;
      if (node.estimate && node.estimate > 0) {
        pTotal += node.estimate;
        if (node.status === 'done') pDone += node.estimate;
      }
      return;
    }
    for (const c of children) walk(c, depth + 1);
  };
  walk(item, 0);
  // A parent marked done counts as done whatever its children say.
  const fraction = item.status === 'done' ? 1 : weight ? got / weight : 0;
  return { done, total, fraction, points: { done: pDone, total: pTotal } };
}

// ---------------------------------------------------------------------------
// Validation (shared by the hub and the console)
// ---------------------------------------------------------------------------

export const TITLE_MAX = 200;
export const BODY_MAX = 20_000;
export const GOAL_MAX = 4_000;
export const NOTE_MAX = 4_000;
export const CRITERIA_MAX = 50;
export const CRITERION_MAX = 1_000;
export const LABELS_MAX = 20;
export const LABEL_MAX = 40;
export const DEFECT_FIELD_MAX = 8_000;
export const ESTIMATE_MAX = 1_000;

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

function tagsError(v: unknown, what: string): string | null {
  if (!Array.isArray(v)) return `${what} must be a list of words.`;
  if (v.length > LABELS_MAX) return `Keep ${what.toLowerCase()} to ${LABELS_MAX} or fewer.`;
  for (const t of v) if (typeof t !== 'string' || !t.trim() || t.length > LABEL_MAX) return `Each of the ${what.toLowerCase()} must be text under ${LABEL_MAX} characters.`;
  return null;
}

/** A problem with a list of acceptance criteria, or null. */
export function acceptanceError(v: unknown): string | null {
  if (!Array.isArray(v)) return 'Acceptance criteria must be a list.';
  if (v.length > CRITERIA_MAX) return `Keep acceptance criteria to ${CRITERIA_MAX} or fewer. Split the item instead.`;
  for (const c of v) {
    if (!isObj(c)) return 'Each acceptance criterion must be an object.';
    if (c.format !== undefined && c.format !== 'gwt' && c.format !== 'check') return 'An acceptance criterion is Given/When/Then (gwt) or a checklist line (check).';
    for (const k of ['text', 'given', 'when', 'then'] as const) {
      if (c[k] !== undefined && (typeof c[k] !== 'string' || (c[k] as string).length > CRITERION_MAX)) return `Keep each acceptance criterion under ${CRITERION_MAX} characters.`;
    }
    if (typeof c.text !== 'string') return 'Each acceptance criterion needs its text.';
    const body = c.format === 'gwt' ? `${c.given ?? ''}${c.when ?? ''}${c.then ?? ''}${c.text}` : c.text;
    if (!body.trim()) return 'An acceptance criterion is empty. Write it or remove it.';
    if (c.done !== undefined && typeof c.done !== 'boolean') return 'An acceptance criterion is checked (true) or not (false).';
  }
  return null;
}

export function defectError(v: unknown): string | null {
  if (v === null) return null;
  if (!isObj(v)) return 'Defect details must be an object.';
  if (v.severity !== undefined && !SEVERITIES.includes(v.severity as Severity)) return 'Severity must be critical, major, minor or trivial.';
  for (const k of ['environment', 'steps', 'expected', 'actual'] as const) {
    if (v[k] !== undefined && (typeof v[k] !== 'string' || (v[k] as string).length > DEFECT_FIELD_MAX)) return `Keep each defect field under ${DEFECT_FIELD_MAX} characters.`;
  }
  return null;
}

/** A plain-language problem with a create or update body, or null when it is fine. */
export function itemInputError(v: unknown, partial = false): string | null {
  if (!isObj(v)) return 'Send the item as a JSON object.';
  if (!partial || 'projectId' in v) {
    if (typeof v.projectId !== 'string' || !v.projectId) return 'Choose a project for the item.';
  }
  if (!partial || 'kind' in v) {
    if (!normalizeKind(v.kind)) return 'The kind must be epic, feature, story, task, defect, spike or chore.';
  }
  if (!partial || 'title' in v) {
    if (typeof v.title !== 'string' || !v.title.trim()) return 'Give the item a title.';
    if (v.title.length > TITLE_MAX) return `Keep the title under ${TITLE_MAX} characters.`;
  }
  if ('body' in v && v.body !== undefined && (typeof v.body !== 'string' || v.body.length > BODY_MAX)) return `The description must be text under ${BODY_MAX} characters.`;
  if ('status' in v && v.status !== undefined && !ITEM_STATUSES.includes(v.status as ItemStatus)) return 'That status is not one Orgus knows.';
  if ('priority' in v && v.priority !== undefined && !ITEM_PRIORITIES.includes(v.priority as ItemPriority)) return 'Priority must be 0 (urgent) to 3 (low).';
  if ('owner' in v && v.owner !== undefined && v.owner !== null && (typeof v.owner !== 'string' || v.owner.length > 100)) return 'The owner must be a name under 100 characters.';
  if ('parentId' in v && v.parentId !== undefined && v.parentId !== null && typeof v.parentId !== 'string') return 'The parent must be an item id.';
  if ('rank' in v && v.rank !== undefined && (typeof v.rank !== 'number' || !Number.isFinite(v.rank))) return 'The rank must be a number.';
  if ('acceptance' in v && v.acceptance !== undefined) {
    const e = acceptanceError(v.acceptance);
    if (e) return e;
  }
  if ('estimate' in v && v.estimate !== undefined && v.estimate !== null && (typeof v.estimate !== 'number' || !Number.isFinite(v.estimate) || v.estimate < 0 || v.estimate > ESTIMATE_MAX)) {
    return `The estimate must be a number of points from 0 to ${ESTIMATE_MAX}.`;
  }
  if ('labels' in v && v.labels !== undefined) {
    const e = tagsError(v.labels, 'Labels');
    if (e) return e;
  }
  if ('components' in v && v.components !== undefined) {
    const e = tagsError(v.components, 'Components');
    if (e) return e;
  }
  if ('defect' in v && v.defect !== undefined) {
    const e = defectError(v.defect);
    if (e) return e;
  }
  if ('aiDrafted' in v && v.aiDrafted !== undefined && typeof v.aiDrafted !== 'boolean') return 'aiDrafted must be true or false.';
  if ('override' in v && v.override !== undefined) {
    if (!isObj(v.override) || typeof v.override.reason !== 'string' || !v.override.reason.trim()) return 'Say why you are overriding the status the mission set.';
    if (v.override.reason.length > 500) return 'Keep the reason under 500 characters.';
  }
  if ('cited' in v && v.cited !== undefined && v.cited !== null) return 'cited can only be set to null, which removes the citation.';
  if ('pinned' in v && v.pinned !== undefined && v.pinned !== false) return 'pinned can only be set to false, which hands the status back to the mission.';
  return null;
}

export function linkInputError(v: unknown): string | null {
  if (!isObj(v)) return 'Send the link as a JSON object.';
  if (!LINK_TYPES.includes(v.type as LinkType)) return 'The link type must be blocks, relates, duplicates, implements or affects.';
  if (typeof v.from !== 'string' || !v.from || typeof v.to !== 'string' || !v.to) return 'Say which two items to link.';
  if (v.from === v.to) return 'An item cannot link to itself.';
  if (v.inverse !== undefined && typeof v.inverse !== 'boolean') return 'inverse must be true or false.';
  return null;
}

export function missionInputError(v: unknown, partial = false): string | null {
  if (!isObj(v)) return 'Send the mission as a JSON object.';
  if (!partial) {
    if (typeof v.projectId !== 'string' || !v.projectId) return 'Choose a project for the mission.';
  }
  if (!partial || 'title' in v) {
    if (typeof v.title !== 'string' || !v.title.trim()) return 'Give the mission a title.';
    if (v.title.length > TITLE_MAX) return `Keep the title under ${TITLE_MAX} characters.`;
  }
  if (!partial || 'goal' in v) {
    if (typeof v.goal !== 'string' || !v.goal.trim()) return 'Say what done looks like: the goal.';
    if (v.goal.length > GOAL_MAX) return `Keep the goal under ${GOAL_MAX} characters.`;
  }
  if ('kind' in v && v.kind !== undefined && v.kind !== 'work' && v.kind !== 'review') return 'The mission kind must be work or review.';
  if ('budget' in v && v.budget !== undefined && v.budget !== null && (typeof v.budget !== 'number' || !Number.isFinite(v.budget) || v.budget <= 0 || v.budget > 1e6)) {
    return 'The budget must be a positive amount in USD.';
  }
  if ('budgetHardStop' in v && v.budgetHardStop !== undefined && typeof v.budgetHardStop !== 'boolean') return 'The hard stop must be true or false.';
  if ('model' in v && v.model !== undefined && v.model !== null && (typeof v.model !== 'string' || !/^[\w.:/@-]{1,100}$/.test(v.model))) return 'That model name is not valid.';
  if ('itemId' in v && v.itemId !== undefined && v.itemId !== null && typeof v.itemId !== 'string') return 'The item must be an item id.';
  if ('closed' in v && v.closed !== undefined && v.closed !== null && v.closed !== 'done' && v.closed !== 'stopped') return 'A mission closes as done or stopped.';
  return null;
}

export function missionStartError(v: unknown): string | null {
  if (!isObj(v)) return 'Send the start request as a JSON object.';
  if (typeof v.machineId !== 'string' || !v.machineId) return 'Choose a machine to run on.';
  const hasCheckout = typeof v.checkoutId === 'string' && !!v.checkoutId;
  const hasCwd = typeof v.cwd === 'string' && !!v.cwd;
  if (!hasCheckout && !hasCwd) return 'Choose a checkout to run in.';
  if (v.newWorktree !== undefined) {
    if (!isObj(v.newWorktree) || typeof v.newWorktree.branch !== 'string' || !v.newWorktree.branch.trim()) return 'A new worktree needs a branch name.';
  }
  if (v.mode !== undefined && v.mode !== 'headless' && v.mode !== 'terminal') return 'The mode must be headless or terminal.';
  if (v.model !== undefined && v.model !== null && (typeof v.model !== 'string' || !/^[\w.:/@-]{1,100}$/.test(v.model))) return 'That model name is not valid.';
  if (v.permissionMode !== undefined && typeof v.permissionMode !== 'string') return 'That permission mode is not valid.';
  if (v.note !== undefined && (typeof v.note !== 'string' || v.note.length > NOTE_MAX)) return `Keep the note under ${NOTE_MAX} characters.`;
  if (v.budget !== undefined && v.budget !== null && (typeof v.budget !== 'number' || !Number.isFinite(v.budget) || v.budget <= 0 || v.budget > 1e6)) {
    return 'The budget must be a positive amount in USD.';
  }
  if (v.budgetHardStop !== undefined && typeof v.budgetHardStop !== 'boolean') return 'The hard stop must be true or false.';
  if (v.launchAccountId !== undefined && (typeof v.launchAccountId !== 'string' || !/^[\w-]{1,64}$/.test(v.launchAccountId))) return 'That account is not valid.';
  if (v.agentKind !== undefined && (typeof v.agentKind !== 'string' || !/^[a-z0-9._-]{1,40}$/.test(v.agentKind))) return 'That agent kind is not valid.';
  if (v.thoughtLevel !== undefined && (typeof v.thoughtLevel !== 'string' || !/^[A-Za-z0-9._:-]{1,60}$/.test(v.thoughtLevel))) return 'That thought level is not valid.';
  if (v.agentReason !== undefined && (typeof v.agentReason !== 'string' || v.agentReason.length > 300)) return 'The agent reason must be text of at most 300 characters.';
  return null;
}
